import { InnerhivePage } from '../InnerhivePage';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { Redirect, Route } from 'react-router';
import { AppointmentsList } from './AppointmentsList';
import { AppointmentEditorDrawer } from './AppointmentEditorDrawer';
import { IonButtons, IonToolbar } from '@ionic/react';
import { AddAppointmentButton } from './AddAppointmentButton';
import { AppointmentsState, NEW_APPOINTMENT_ID } from './AppointmentsState';
import { useEffect, useRef } from 'react';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { CareRecipientSwitcher } from '../navigation/CareRecipientSwitcher';
import { CalendarTabsProvider } from './CalendarTabsProvider';
import { FullWidthHeader } from '../components/FullWidthHeader';
import { AppointmentsConfigPopover } from './AppointmentsConfigPopover';
import { useSubscription } from '@meraki-internal/state';
import { Text } from '../components/Text';
import { RevenueCatModel } from '../innerhive-plus/revenue-cat/RevenueCatModel';
import { Href } from '../components/Href';
import { ConnectCalendarViewModel } from './ConnectCalendarViewModel';
import { CareMapState } from '../caremap/CareMapState';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { CreateAccountButton } from '../auth/CreateAccountButton';
import { AddCareMapButton } from '../caremap/AddCareMapButton';
import { ViewNoteWithQueryParamDrawer } from '../notes/view-note/ViewNoteWithQueryParamDrawer';

export const AppointmentsPageRouteHandler: React.FC = () => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const recentCareMap = careMapState.getMostRecentCaremap();
    const incompleteCareMap = careMapState.getIncompleteCareMap();

    return (
        <>
            <Route exact path="/appointments/:caremapId" render={routeParams => (
                <Redirect to={`/calendar/appointments/${routeParams.match.params.caremapId}`} />
            )} />

            <Route exact path="/calendar/appointments" render={() => {
                if (recentCareMap && !incompleteCareMap) {
                    return <Redirect to={`/calendar/appointments/${recentCareMap.careMapId}`} />;
                } else {
                    return <AppointmentsNoCareMapPage />;
                }
            }} />

            <Route exact path="/calendar/appointments/:caremapId" render={routeParams => (
                <ActiveCareMapProviderV2 careMapId={routeParams.match.params.caremapId}>
                    <ActiveCaremapAppointmentsPage />
                </ActiveCareMapProviderV2>
            )} />
        </>

    );

};

const emptyImage = <img src="/assets/images/calendar/empty-appointments.svg" alt="no appointments" style={{width: '100%'}} />;

export const AppointmentsNoCareMapPage: React.FC = () => {
    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));
    const tabsProvider = useInstance(CalendarTabsProvider);
    return (
        <InnerhivePage title="Calendar" tabs={tabsProvider.getTabs()} skipRequireName>
            {isAnonymous &&
                <>
                    <CreateAccountButton trackingFrom="Appointments" />
                    <p>Create an account to organize your care schedule and even sync to your calendar app of choice!</p>
                </>
            }
            {!isAnonymous &&
                <>
                    <AddCareMapButton />
                    <p>Add a care recipient to organize your care schedule and even sync to your calendar app of choice!</p>
                    {emptyImage}
                </>
            }
        </InnerhivePage>
    );
};

export const ActiveCaremapAppointmentsPage: React.FC = () => {
    const drawer = useInstance(AppointmentEditorDrawer);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const tabsProvider = useInstance(CalendarTabsProvider);
    const connectCalDialogVM = useInstance(ConnectCalendarViewModel);
    const careMapState = useInstance(CareMapState);
    const viewNoteDrawer = useInstance(ViewNoteWithQueryParamDrawer);

    const appointmentsState = useInstance(AppointmentsState);
    useSubscription(() => appointmentsState);

    const revenueCat = useInstance(RevenueCatModel);
    useSubscription(() => revenueCat);

    const contentRef = useRef<HTMLIonContentElement>(null);

    // NOTE: heightOffset accounts for the fixed header containing switcher,
    // which is taller for anonymous due to expiration message
    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));
    const heightOffset = 80 + (isAnonymous ? 28 : 0);

    const hasInnerhivePlus = revenueCat.hasInnerhivePlus();
    const emptyText = hasInnerhivePlus ? (
        <>
            Track appointments to organize your care schedule and even <Href onClick={() => connectCalDialogVM.open('empty appointments')}>sync to your calendar app</Href> of choice!
        </>
    ) : (
        <>
            Track appointments to organize your care schedule and even sync to your calendar app of choice!
        </>
    );

    useEffect(() => {
        mixpanel.track('Appointments Tab Opened');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const editAppointment = (appointmentId: string) => {
        if (appointmentId === NEW_APPOINTMENT_ID) {
            appointmentsState.trackNewAppointmentOpened({from: 'Appointments Page'});
        }

        drawer.open({ appointmentId });
    };

    const incompleteCareMap = careMapState.getIncompleteCareMap();

    return (
        <>
            <InnerhivePage title="Calendar" tabs={tabsProvider.getTabs()} contentRef={contentRef}>
                <FullWidthHeader fixed>
                    <IonToolbar>
                        {incompleteCareMap ? (
                            <AddCareMapButton />
                        ) : (
                            <>
                                <div style={{margin: '4px 12px 0 4px', paddingTop: 5, paddingBottom: 10}}>
                                    <CareRecipientSwitcher />
                                </div>
                                <IonButtons slot="end">
                                    <AddAppointmentButton
                                        useIcon
                                        onClick={editAppointment}
                                        canAdd={appointmentsState.canAdd()}
                                    />
                                </IonButtons>
                            </>
                        )}
                    </IonToolbar>
                </FullWidthHeader>
                <AppointmentsList
                    hideHeading
                    showEmptyToday
                    heightOffset={heightOffset}
                    contentRef={contentRef}
                    data-type="more-appointments-list"
                    editAppointment={editAppointment}
                    onViewRecording={(noteId) => {
                        viewNoteDrawer.open({ noteId, trackingFrom: 'Appointments Page' });
                    }}
                    customEmptyMsg={(
                        <div style={{paddingTop: 12, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                            <div><Text>{emptyText}</Text></div>
                            {emptyImage}
                        </div>
                    )}
                />
                <AppointmentsConfigPopover />
            </InnerhivePage>
        </>
    );
};
